const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

const request = (url, options = {}) => {
  const headers = {
    "Content-Type": "application/json",
    ...(options.headers || {}),
  };
  return fetch(url, {
    ...options,
    headers,
    credentials: "include",
  });
};

const handleResponse = async (response) => {
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.detail || `HTTP ${response.status}`);
  }
  return response.json();
};

export const api = {
  auth: {
    login: async (email, password) => {
      const response = await request(`${API_BASE}/auth/login`, {
        method: "POST",
        body: JSON.stringify({ email, password }),
      });
      return handleResponse(response);
    },
    logout: async () => {
      const response = await request(`${API_BASE}/auth/logout`, {
        method: "POST",
      });
      return handleResponse(response);
    },
    me: async () => {
      const response = await request(`${API_BASE}/auth/me`);
      return handleResponse(response);
    },
    changePassword: async (oldPassword, newPassword) => {
      const response = await request(`${API_BASE}/auth/change-password`, {
        method: "POST",
        body: JSON.stringify({
          old_password: oldPassword,
          new_password: newPassword,
        }),
      });
      return handleResponse(response);
    },
  },

  registrationTypes: {
    list: async () => handleResponse(await request(`${API_BASE}/registration-types`)),
    create: async (data) => handleResponse(await request(`${API_BASE}/registration-types`, {
      method: "POST",
      body: JSON.stringify(data),
    })),
    update: async (id, data) => handleResponse(await request(`${API_BASE}/registration-types/${id}`, {
      method: "PUT",
      body: JSON.stringify(data),
    })),
    delete: async (id) => handleResponse(await request(`${API_BASE}/registration-types/${id}`, {
      method: "DELETE",
    })),
  },

  registrants: {
    list: async (status = null) => {
      const url = status ? `${API_BASE}/registrants?status=${status}` : `${API_BASE}/registrants`;
      return handleResponse(await request(url));
    },
    stats: async () => handleResponse(await request(`${API_BASE}/registrants/stats`)),
    action: async (id, action) => handleResponse(await request(`${API_BASE}/registrants/${id}/action`, {
      method: "POST",
      body: JSON.stringify({ action }),
    })),
    bulkAction: async (action, registrantIds) => handleResponse(await request(`${API_BASE}/registrants/bulk-action`, {
      method: "POST",
      body: JSON.stringify({ action, registrant_ids: registrantIds }),
    })),
  },

  users: {
    list: async () => handleResponse(await request(`${API_BASE}/users`)),
    create: async (data) => handleResponse(await request(`${API_BASE}/users`, {
      method: "POST",
      body: JSON.stringify(data),
    })),
    update: async (id, data) => handleResponse(await request(`${API_BASE}/users/${id}`, {
      method: "PUT",
      body: JSON.stringify(data),
    })),
    delete: async (id) => handleResponse(await request(`${API_BASE}/users/${id}`, {
      method: "DELETE",
    })),
    resetPassword: async (id, newPassword) => handleResponse(await request(`${API_BASE}/users/${id}/reset-password`, {
      method: "POST",
      body: JSON.stringify({ new_password: newPassword }),
    })),
  },

  dashboard: {
    get: async () => handleResponse(await request(`${API_BASE}/admin/dashboard`)),
  },

  activityLog: {
    list: async ({ page = 1, pageSize = 50, userId = null, status = null } = {}) => {
      const params = new URLSearchParams({ page, page_size: pageSize });
      if (userId) params.append("user_id", userId);
      if (status) params.append("status", status);
      return handleResponse(await request(`${API_BASE}/admin/activity-log?${params}`));
    },
  },

  settings: {
    getApiConfig: async () => handleResponse(await request(`${API_BASE}/eventsair/config`)),
    updateApiConfig: async (config) => handleResponse(await request(`${API_BASE}/eventsair/config`, {
      method: "PUT",
      body: JSON.stringify(config),
    })),
    testConnection: async () => handleResponse(await request(`${API_BASE}/eventsair/test-connection`, {
      method: "POST",
    })),
    getActiveEvent: async () => handleResponse(await request(`${API_BASE}/eventsair/active-event`)),
  },
};
